/**
 * JurisdictionPicker
 * Type-ahead search over the corpus jurisdictions ("Country – Region"), with "Not listed" free text
 * for anything the corpus does not hold. Chosen items are added as tags.
 * Defined outside any parent component to avoid remount-on-render focus loss.
 */
import React, { useEffect, useId, useMemo, useState } from 'react';
import { MapPin } from 'lucide-react';
import { fetchJurisdictionChoices, JurisdictionChoice } from '../../../api/enterpriseApi';
import { choiceValue, matchJurisdictions, MIN_QUERY, SEPARATOR } from './jurisdictionSearch';
import styles from './JurisdictionPicker.module.css';

interface Props {
  label: string;
  tags: string[];
  onAdd: (value: string) => void;
  onRemove: (index: number) => void;
}

type Status = 'loading' | 'ready' | 'error';

export function JurisdictionPicker({ label, tags, onAdd, onRemove }: Props) {
  const uid = useId();
  const inputId = `${uid}-input`;
  const listId = `${uid}-list`;

  const [choices, setChoices] = useState<JurisdictionChoice[]>([]);
  const [status, setStatus] = useState<Status>('loading');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const [notListed, setNotListed] = useState(false);
  const [otherCountry, setOtherCountry] = useState('');
  const [otherRegion, setOtherRegion] = useState('');

  useEffect(() => {
    fetchJurisdictionChoices()
      .then((list) => { setChoices(list); setStatus('ready'); })
      .catch(() => setStatus('error'));
  }, []);

  const matches = useMemo(() => matchJurisdictions(choices, query, tags), [choices, query, tags]);
  const showList = open && query.trim().length >= MIN_QUERY;

  const add = (value: string) => {
    const v = value.trim();
    if (!v || tags.includes(v)) return;
    onAdd(v);
  };

  const pick = (c: JurisdictionChoice) => {
    add(choiceValue(c));
    setQuery('');
    setOpen(false);
    setActive(0);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, Math.max(matches.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (showList && matches[active]) {
        e.preventDefault();
        pick(matches[active]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const otherValue = `${otherCountry.trim()}${SEPARATOR}${otherRegion.trim() || otherCountry.trim()}`;
  const otherReady = otherCountry.trim() !== '' && !tags.includes(otherValue);

  const addOther = () => {
    if (!otherReady) return;
    add(otherValue);
    setOtherCountry('');
    setOtherRegion('');
  };

  const emptyMessage =
    status === 'loading' ? 'Loading places…'
    : status === 'error' ? 'Suggestions are unavailable right now. Use “Not listed” below.'
    : 'No match. Try another spelling, or use “Not listed” below.';

  return (
    <div className={styles.root}>
      <label className={styles.label} htmlFor={inputId}>{label}</label>

      <div className={styles.searchWrap}>
        <input
          id={inputId}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={showList}
          aria-controls={listId}
          aria-activedescendant={showList && matches[active] ? `${listId}-${active}` : undefined}
          className={styles.input}
          value={query}
          placeholder="Type a country or region, e.g. India"
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
        />

        {showList && (
          <ul id={listId} role="listbox" aria-label={`${label}: suggestions`} className={styles.list}>
            {matches.map((c, i) => (
              <li
                key={choiceValue(c)}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                className={i === active ? styles.optionActive : styles.option}
                // keep focus in the input so the click lands before the blur closes the list
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(c)}
              >
                <span className={styles.optionName}>{c.name}</span>
                <span className={styles.optionMeta}>
                  {[c.country !== c.name ? c.country : '', c.type !== 'country' ? c.type : ''].filter(Boolean).join(' · ')}
                </span>
              </li>
            ))}
            {matches.length === 0 && <li role="presentation" className={styles.empty}>{emptyMessage}</li>}
          </ul>
        )}
      </div>

      <button
        type="button"
        className={styles.linkBtn}
        aria-expanded={notListed}
        onClick={() => setNotListed((v) => !v)}
      >
        {notListed ? 'Back to search' : 'Not listed? Enter it yourself'}
      </button>

      {notListed && (
        <div className={styles.otherBox}>
          <input
            className={styles.input}
            aria-label={`${label}: country`}
            placeholder="Country"
            value={otherCountry}
            onChange={(e) => setOtherCountry(e.target.value)}
          />
          <input
            className={styles.input}
            aria-label={`${label}: region or state`}
            placeholder="Region or state (optional)"
            value={otherRegion}
            onChange={(e) => setOtherRegion(e.target.value)}
          />
          <div className={styles.previewRow}>
            <span className={styles.preview}>
              <MapPin size={16} aria-hidden /> {otherCountry.trim() ? otherValue : 'Enter a country to add it'}
            </span>
            <button type="button" className={styles.addBtn} onClick={addOther} disabled={!otherReady}>
              Add
            </button>
          </div>
        </div>
      )}

      {tags.length > 0 && (
        <div className={styles.tags}>
          {tags.map((t, i) => (
            <span key={i} className={styles.tag}>
              <MapPin size={16} aria-hidden /> {t}
              <button type="button" onClick={() => onRemove(i)} aria-label={`Remove ${t}`}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
